import { useState } from "react"
import Image from "next/image"
import classNames from "classnames"
import { AlertContainer } from "@nova/component-alert-container"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { config } from "@nova/configuration/browser"
import { useAppDispatch } from "integrations/redux/hooks"
import { usePluginState } from "../../usePluginState"
import { hideGlobalModal } from "runlevel/actions"
import { animationClass } from "features/data-management/workspace/constants"

const OCA_ALERT_DISMISSED_KEY = "dji-oca-modal-alert-dismissed"

const getOcaUrl = () => {
    const env = config.get("environmentName")
    const subdomain = env === "production" ? "app" : env
    return `https://dji.${subdomain}.mapnova.com`
}

export const DjiEnterpriseOcaModal = () => {
    const { setSelectedPlugin } = usePluginState()
    const dispatch = useAppDispatch()
    const [tutorialOpen, setTutorialOpen] = useState(false)
    const [showAlert, setShowAlert] = useState(
        localStorage.getItem(OCA_ALERT_DISMISSED_KEY) !== "true"
    )

    const close = () => {
        dispatch(hideGlobalModal())
        setSelectedPlugin(undefined)
    }

    return (
        <HighPriorityNovaModalContainer
            title="Start a Live Stream"
            className="w-[34rem]"
            canCloseModal
            closeModal={close}
        >
            <div className="flex flex-col pt-4 text-gray-0">
                {showAlert && (
                    <AlertContainer
                        type="info"
                        title="New Live Stream Link"
                        body="Use the new link below to navigate your workspace and create Live Streams, Orthomosaics & Hotspots directly from your controller."
                        className="mb-0"
                        onClose={() => {
                            localStorage.setItem(OCA_ALERT_DISMISSED_KEY, "true")
                            setShowAlert(false)
                        }}
                    />
                )}
                <div className="pt-[20px] pb-[32px]">
                    <Image
                        src="/images/logos/image.webp"
                        alt="dji-logo"
                        width={39}
                        height={23}
                        className="mx-auto select-none"
                    />

                    <div className="flex flex-col items-center gap-2">
                        <small className="text-gray-400 tracking-widest">
                            ENTER INTO DJI CONTROLLER:
                        </small>
                        <h4 className="font-bold">{getOcaUrl()}</h4>
                    </div>
                </div>

                <div className="pt-4 flex flex-col">
                    <small className="text-gray-400 tracking-widest">NEED HELP?</small>
                    <div
                        className="flex items-center cursor-pointer py-[8px]"
                        onClick={() => setTutorialOpen((open) => !open)}
                    >
                        <Icons.Icon
                            Icon={Icons.ArrowDropdownRight}
                            className={classNames(animationClass, "cursor-pointer", {
                                "rotate-90": tutorialOpen,
                                "text-white-400 group-hover:text-gray-0": !tutorialOpen,
                            })}
                        />
                        <h4 className="text-gray-100">Tutorial</h4>
                    </div>
                    <ScrollableArea
                        className={classNames(
                            {
                                "h-auto max-h-[300px]": tutorialOpen,
                                "h-0": !tutorialOpen,
                            },
                            "flex flex-col gap-2"
                        )}
                    >
                        <div>1. On your DJI Controller, tap Cloud Service.</div>
                        <Image
                            src="/images/dji-controller-app-tutorial/1.png"
                            width={416}
                            height={233}
                            alt="step-1"
                        />
                        <div className="mt-4">2. Tap on the Open Platforms button.</div>
                        <Image
                            src="/images/dji-controller-app-tutorial/2.png"
                            width={416}
                            height={233}
                            alt="step-2"
                        />
                        <div className="mt-4">
                            3. Type in the link:&nbsp;
                            {getOcaUrl()}
                        </div>
                        <Image
                            src="/images/dji-controller-app-tutorial/3.png"
                            width={416}
                            height={235}
                            alt="step-3"
                        />
                        <div className="mt-4">
                            4. Login to your account. Set a password if you use Google, Apple, or
                            Microsoft to sign in.
                        </div>
                        <Image
                            src="/images/dji-controller-app-tutorial/4.png"
                            width={416}
                            height={235}
                            alt="step-4"
                        />
                        <div className="mt-4">5. Tap “+ Project” to create a project.</div>
                        <Image
                            src="/images/dji-controller-app-tutorial/5.png"
                            width={416}
                            height={235}
                            alt="step-4"
                        />
                        <div className="mt-4">6. Select Live Stream and tap Confirm.</div>
                        <Image
                            src="/images/dji-controller-app-tutorial/6.png"
                            width={416}
                            height={235}
                            alt="step-4"
                        />
                        <div className="mt-4">7. Tap Start Session.</div>
                        <Image
                            src="/images/dji-controller-app-tutorial/7.png"
                            width={416}
                            height={235}
                            alt="step-4"
                        />
                    </ScrollableArea>
                </div>

                <div className="flex w-full justify-end items-center p-[12px]">
                    <ButtonTertiary title="Close" onClick={close} />
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}
